import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

// Demo-only floating guide: the mascot sits bottom-right and explains the
// page you're on. Auto-opens the first time you visit each page (per browser
// session), can be dismissed per-page, or hidden entirely for the session.
// Mounted OUTSIDE the router (App root), so the current path is tracked by
// polling window.location — cheap, demo-only, and avoids threading the
// component into every shell layout.

const TIPS = [
  // Staff (provider + law firm)
  { match: '/app/dashboard',   title: 'Your dashboard',  text: 'A live snapshot of the practice — today’s appointments, recent activity and quick links. Law firms see their caseload here instead.' },
  { match: '/app/contacts/',   title: 'Client profile',  text: 'The full PI picture: accident details, diagnoses, insurance and adjusters, plus documents. Try the Upload document button on the Documents tab.' },
  { match: '/app/contacts',    title: 'Contacts',        text: 'Every patient (or client, for law firms) in one place. Click Jordan Smith to see a complete profile with case, medical and insurance details.' },
  { match: '/app/cases/new',   title: 'New case',        text: 'Pick the client, drop in the crash report, ER records and PIP log, and let the AI fill the case. Review each field’s confidence, then create.' },
  { match: '/app/cases/',      title: 'Case file',       text: 'The matter and its funding path: what to bill this visit to, PIP remaining, the Emergency Medical Condition record, and the alerts the Florida rules raise.' },
  { match: '/app/cases',       title: 'Cases',           text: 'One row per matter — status, date of injury, what’s paying for treatment. A client with two accidents shows twice. Find a Provider starts a referral from the case.' },
  { match: '/app/scheduling',  title: 'Scheduling',      text: 'Drag to book, color-coded by appointment type, per location. Today’s ortho exam for Jordan Smith is shared with his law firm — that’s the referral network at work.' },
  { match: '/app/directory/',  title: 'Practice profile', text: 'A partner organization’s public profile. From here you can request a partnership or send a referral.' },
  { match: '/app/directory',   title: 'Directory & network', text: 'Every law firm and practice on the platform, on a map. The Network tab shows your accepted partnerships — the orgs you collaborate with.' },
  { match: '/app/documents',   title: 'Documents',       text: 'HIPAA-conscious file sharing between partnered organizations, with a full audit trail. Uploads here are org-wide; patient files live on their profile.' },
  { match: '/app/forms',       title: 'Forms',           text: 'Upload a PDF and place signature fields (like DocuSign), or use the Form builder to create your own questionnaires from scratch — patients fill them right in their portal.' },
  { match: '/app/tbi-assessments', title: 'TBI assessments', text: 'Conversational MoCA cognitive screening for TBI cases — a differentiator for PI practices.' },
  { match: '/app/settings',    title: 'Settings',        text: 'Appointment types, locations, staff, billing and branding — everything is configurable per organization.' },
  { match: '/app',             title: 'Staff workspace', text: 'This is the staff side of MediLink. Use the left nav to explore — the demo resets nightly, so click anything.' },
  // Patient
  { match: '/client/forms',     title: 'Forms',          text: 'Forms sent by your provider or law firm, signed right on the document — no printing, no scanning.' },
  { match: '/client/directory', title: 'Find a provider', text: 'Patients can browse the same network map to find nearby practices.' },
  { match: '/client/documents', title: 'My documents',   text: 'Everything the patient has signed or been sent, in one place.' },
  { match: '/client',           title: 'Patient view',   text: 'This is what Jordan Smith sees: upcoming appointments, forms waiting for a signature, and his providers.' },
  // Admin
  { match: '/admin',            title: 'Platform admin', text: 'The operator’s view — every organization, user, subscription and a live map of the whole network.' },
];

function tipFor(path) {
  return TIPS.find(t => path.startsWith(t.match)) || null;
}

export default function DemoGuide() {
  const isDemo = process.env.REACT_APP_DEMO === 'true';
  const [path, setPath] = useState(window.location.pathname);
  const [hasSession, setHasSession] = useState(false);
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem('demo-guide-hidden') === '1');
  const seenRef = useRef(new Set());

  // Track SPA navigation (router is inaccessible from here)
  useEffect(() => {
    if (!isDemo) return;
    const t = setInterval(() => {
      setPath(p => (window.location.pathname !== p ? window.location.pathname : p));
    }, 400);
    return () => clearInterval(t);
  }, [isDemo]);

  useEffect(() => {
    if (!isDemo) return;
    supabase.auth.getSession().then(({ data }) => setHasSession(!!data?.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => setHasSession(!!session));
    return () => sub.subscription.unsubscribe();
  }, [isDemo]);

  const tip = useMemo(() => tipFor(path), [path]);

  // Auto-open on first visit to each page this session
  useEffect(() => {
    if (!tip || hidden) return;
    if (!seenRef.current.has(tip.match)) {
      seenRef.current.add(tip.match);
      setOpen(true);
    }
  }, [tip, hidden]);

  if (!isDemo || !hasSession || !tip || hidden) return null;

  // Phones: bottom-LEFT, so the mascot never sits on a page's own floating
  // action button (Scheduler's "+" lives bottom-right).
  const phone = typeof window !== 'undefined' && window.innerWidth < 640;

  return (
    <div style={{
      position: 'fixed', right: phone ? 'auto' : 16, left: phone ? 12 : 'auto', bottom: 44, zIndex: 2147482000,
      display: 'flex', flexDirection: 'column', alignItems: phone ? 'flex-start' : 'flex-end', gap: 10,
      pointerEvents: 'none',
    }}>
      {open && (
        <div style={{
          pointerEvents: 'auto', width: 'min(290px, calc(100vw - 32px))', borderRadius: 16,
          background: '#fff', color: '#101733',
          boxShadow: '0 20px 45px -15px rgba(16,23,51,0.45), 0 0 0 1px rgba(16,23,51,0.06)',
          padding: '14px 16px', position: 'relative',
        }}>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close tip"
            style={{
              position: 'absolute', top: 8, right: 8, border: 'none', background: 'none',
              cursor: 'pointer', color: '#9ca3af', padding: 4,
            }}
          >
            <X size={14} />
          </button>
          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 4, color: '#22346A' }}>{tip.title}</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.55, color: '#374151' }}>{tip.text}</div>
          <button
            onClick={() => { sessionStorage.setItem('demo-guide-hidden', '1'); setHidden(true); }}
            style={{
              marginTop: 10, border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 11, color: '#9ca3af', textDecoration: 'underline', padding: 0,
            }}
          >
            Hide guide for this session
          </button>
          {/* speech-bubble tail */}
          <div style={{
            position: 'absolute', right: phone ? 'auto' : 26, left: phone ? 26 : 'auto', bottom: -7, width: 14, height: 14,
            background: '#fff', transform: 'rotate(45deg)',
            boxShadow: '3px 3px 6px -2px rgba(16,23,51,0.15)',
          }} />
        </div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Demo guide"
        style={{
          pointerEvents: 'auto', border: 'none', background: 'none', cursor: 'pointer',
          padding: 0, lineHeight: 0,
          filter: 'drop-shadow(0 10px 18px rgba(16,23,51,0.35))',
          transition: 'transform .15s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.06)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
      >
        <img src="/demo-mascot.png" alt="Demo guide mascot" style={{ width: phone ? 48 : 74 }} />
      </button>
    </div>
  );
}
